import { StorefrontInsertPosition } from "@redotech/crdb-enums/conversion/storefront-insert-position";
import { CoverageAttachmentStrategy } from "@redotech/crdb-enums/coverage/coverage-attachment-strategy";
import { CoverageCartModificationKind } from "@redotech/crdb-enums/coverage/coverage-cart-modification-kind";
import { CoverageCartRefreshActionType } from "@redotech/crdb-enums/coverage/coverage-cart-refresh-action-type";
import { CoverageCheckoutButtonsFeeProductBehavior } from "@redotech/crdb-enums/coverage/coverage-checkout-buttons-fee-product-behavior";
import { CoverageInfoModalVersion } from "@redotech/crdb-enums/coverage/coverage-info-modal-version";
import { StorefrontWidgetButtonSettingPerProduct } from "@redotech/crdb-enums/coverage/storefront-widget-button-setting-per-product";
import { StorefrontWidgetCartCardIcon } from "@redotech/crdb-enums/coverage/storefront-widget-cart-card-icon";
import { StorefrontWidgetCheckboxIconPosition } from "@redotech/crdb-enums/coverage/storefront-widget-checkbox-icon-position";
import { StorefrontWidgetCheckoutButtonKind } from "@redotech/crdb-enums/coverage/storefront-widget-checkout-button-kind";
import { StorefrontWidgetCheckoutSpacing } from "@redotech/crdb-enums/coverage/storefront-widget-checkout-spacing";
import { StorefrontWidgetCheckoutTextSize } from "@redotech/crdb-enums/coverage/storefront-widget-checkout-text-size";
import { StorefrontWidgetImageElement } from "@redotech/crdb-enums/coverage/storefront-widget-image-element";
import { StorefrontWidgetPlacementContainer } from "@redotech/crdb-enums/coverage/storefront-widget-placement-container";
import { StorefrontWidgetPlacementType } from "@redotech/crdb-enums/coverage/storefront-widget-placement-type";
import { StorefrontWidgetPriceLocation } from "@redotech/crdb-enums/coverage/storefront-widget-price-location";
import { StorefrontWidgetType } from "@redotech/crdb-enums/coverage/storefront-widget-type";
import * as z from "zod";

export const CoverageInfoModalSchema = z.object({
  cartIconEnabled: z.boolean(),
  checkoutIconEnabled: z.boolean(),
  enableTopLayerModalApi: z.boolean(),
  logoUrl: z.string(),
  sideImageUrl: z.string(),
  version: z.enum(CoverageInfoModalVersion),
  headerText: z.string(),
  subheaderText: z.string(),
  line1TitleText: z.string(),
  line1SubtitleText: z.string(),
  line2TitleText: z.string(),
  line2SubtitleText: z.string(),
  line3TitleText: z.string(),
  line3SubtitleText: z.string(),
  line4TitleText: z.string(),
  line4SubtitleText: z.string(),
  customCss: z.string(),
});
export type CoverageInfoModal = z.infer<typeof CoverageInfoModalSchema>;

export const CoverageSettingsSchema = z.object({
  attachmentStrategy: z.enum(CoverageAttachmentStrategy),
  cartElementImageUrl: z.string().nullable(),
  termsBlockCheckoutIfNotAccepted: z.boolean(),
  termsCheckboxSelector: z.string(),
  termsBlockedMessageText: z.string(),
  groupNearbyToggleElementsTogether: z.boolean(),
  usingCartAndCheckoutToggle: z.boolean(),
  inspiredDonationEnabled: z.boolean(),
  forceDoubleCheckoutToggle: z.boolean(),
  useBuyNowCoverage: z.boolean(),
  disableBusinessAccountInStorefront: z.boolean(),
  dispatchResizeAfterItemsPlaced: z.boolean(),
  clickStandardCheckoutButtonEnabled: z.boolean(),
  clickStandardCheckoutButtonSelector: z.string(),
  hideCheckoutUiWhenOptedIn: z.boolean(),
  checkoutButtonsFeeProductBehavior: z
    .enum(CoverageCheckoutButtonsFeeProductBehavior)
    .nullable(),
  openShadowRoot: z.boolean(),
  useShadowRootInCart: z.boolean(),
  cartShadowRootSelector: z.string(),
  hideRedoProductEnabled: z.boolean(),
  hideRedoProductSelector: z.string(),
  hideRedoCheckoutButtonSelector: z.string(),
  customerTagOverrideEnabled: z.boolean(),
  customerTagOverrideTags: z.array(z.string()),
  customerTagOverrideTitle: z.string(),
  customerTagOverrideSubtext: z.string(),
});
export type CoverageSettings = z.infer<typeof CoverageSettingsSchema>;

export const CoverageCartRefreshActionSchema = z.object({
  sortIndex: z.number().int(),
  type: z.enum(CoverageCartRefreshActionType),
  value: z.string(),
});
export type CoverageCartRefreshAction = z.infer<
  typeof CoverageCartRefreshActionSchema
>;

export const CoverageCartModificationSchema = z.object({
  kind: z.enum(CoverageCartModificationKind),
  sortIndex: z.number().int(),
  selector: z.string(),
  search: z.string(),
  replace: z.string(),
});
export type CoverageCartModification = z.infer<
  typeof CoverageCartModificationSchema
>;

export const StorefrontWidgetPlacementSchema = z.object({
  placementIndex: z.number().int(),
  cssSelector: z.string(),
  insertionPosition: z.enum(StorefrontInsertPosition),
  shadowRootSelector: z.string().nullable(),
  hidingElementSelector: z.string().nullable(),
  containerType: z.enum(StorefrontWidgetPlacementContainer),
  displayName: z.string().nullable(),
});
export type StorefrontWidgetPlacement = z.infer<
  typeof StorefrontWidgetPlacementSchema
>;

/** Shared by the widgets that came from shopifyExtension.widgetConfigs. */
export const StorefrontWidgetConfigCommonSchema = z.object({
  placementType: z.enum(StorefrontWidgetPlacementType),
  placeInsideAndOutsideShadowRoot: z.boolean(),
  dataTarget: z.string().nullable(),
});
export type StorefrontWidgetConfigCommon = z.infer<
  typeof StorefrontWidgetConfigCommonSchema
>;

/** Toggle text for the returns, package protection, and both-product cases. */
export const ToggleTextSchema = z.object({
  returnsTitleText: z.string(),
  returnsPositiveSubtext: z.string(),
  returnsNegativeSubtext: z.string(),
  packageProtectionTitleText: z.string(),
  packageProtectionPositiveSubtext: z.string(),
  packageProtectionNegativeSubtext: z.string(),
  bothTitleText: z.string(),
  bothPositiveSubtext: z.string(),
  bothNegativeSubtext: z.string(),
});
export type ToggleText = z.infer<typeof ToggleTextSchema>;

export const ToggleColorsSchema = z.object({
  textColor: z.string(),
  onColor: z.string(),
  offColor: z.string(),
  circleColor: z.string(),
});
export type ToggleColors = z.infer<typeof ToggleColorsSchema>;

export const StorefrontWidgetInfoCardSchema =
  StorefrontWidgetConfigCommonSchema.extend({
    type: z.literal(StorefrontWidgetType.InfoCard),
    returnsTitleText: z.string(),
    returnsSubtitleText: z.string(),
    packageProtectionTitleText: z.string(),
    packageProtectionSubtitleText: z.string(),
    bothTitleText: z.string(),
    bothSubtitleText: z.string(),
    showInfoIcon: z.boolean(),
    badgeWord: z.string(),
    color: z.string(),
    titleFontSize: z.number(),
    titleFontColor: z.string(),
    subtitleFontSize: z.number(),
    subtitleFontColor: z.string(),
    imageElement: z.enum(StorefrontWidgetImageElement),
    priceLocation: z.enum(StorefrontWidgetPriceLocation),
    showBackground: z.boolean(),
    customCss: z.string(),
    attributes: z.array(z.object({ name: z.string(), value: z.string() })),
  });

export const StorefrontWidgetBothCheckoutButtonsSchema =
  StorefrontWidgetConfigCommonSchema.extend({
    type: z.literal(StorefrontWidgetType.BothCheckoutButtons),
    coverageReturnsTitleText: z.string(),
    coverageReturnsSubtitleText: z.string(),
    coveragePackageProtectionTitleText: z.string(),
    coveragePackageProtectionSubtitleText: z.string(),
    coverageBothTitleText: z.string(),
    coverageBothSubtitleText: z.string(),
    nonCoverageReturnsText: z.string(),
    nonCoveragePackageProtectionText: z.string(),
    nonCoverageBothText: z.string(),
    buttonColor: z.string(),
    fontColor: z.string(),
    subtitleFontColor: z.string(),
    fontSize: z.number(),
    subtitleFontSize: z.number(),
    nonCoverageFontColor: z.string(),
    nonCoverageFontSize: z.number(),
    includeNonCoverageUnderline: z.boolean(),
    buttonSettingPerProduct: z.enum(StorefrontWidgetButtonSettingPerProduct),
    optOutButtonFirst: z.boolean(),
    clickStandardCheckoutButtonEnabled: z.boolean(),
    clickStandardCheckoutButtonSelector: z.string(),
    customCss: z.string(),
  });

export const StorefrontWidgetCheckoutButtonSchema =
  StorefrontWidgetConfigCommonSchema.extend({
    type: z.literal(StorefrontWidgetType.CheckoutButton),
    kind: z.enum(StorefrontWidgetCheckoutButtonKind),
    content: z.string(),
    clickStandardCheckoutButtonEnabled: z.boolean(),
    clickStandardCheckoutButtonSelector: z.string(),
    customCss: z.string(),
  });

export const StorefrontWidgetCartCheckboxSchema =
  StorefrontWidgetConfigCommonSchema.extend(ToggleTextSchema.shape).extend({
    type: z.literal(StorefrontWidgetType.CartCheckbox),
    textColor: z.string(),
    fontSize: z.number().nullable(),
    accentColor: z.string(),
    iconPosition: z.enum(StorefrontWidgetCheckboxIconPosition),
    customCss: z.string(),
    autoCheck: z.boolean(),
  });

export const StorefrontWidgetSingleToggleSchema = ToggleTextSchema.extend(
  ToggleColorsSchema.shape,
).extend({
  type: z.literal(StorefrontWidgetType.SingleToggle),
  textAfterRedo: z.string(),
  subtextEnabled: z.boolean(),
  fontSize: z.number().nullable(),
  customCss: z.string(),
  autoCheck: z.boolean(),
  clickListenerSelector: z.string().nullable(),
});

export const StorefrontWidgetDoubleToggleSchema = ToggleTextSchema.omit({
  bothTitleText: true,
  bothPositiveSubtext: true,
  bothNegativeSubtext: true,
})
  .extend(ToggleColorsSchema.shape)
  .extend({
    type: z.literal(StorefrontWidgetType.DoubleToggle),
    fontSize: z.number().nullable(),
    customCss: z.string(),
    returnsAutoCheck: z.boolean(),
    packageProtectionAutoCheck: z.boolean(),
    clickListenerSelector: z.string().nullable(),
  });

export const StorefrontWidgetCartCardSchema = ToggleTextSchema.extend({
  type: z.literal(StorefrontWidgetType.CartCard),
  textColor: z.string(),
  fontSize: z.number().nullable(),
  customCss: z.string(),
  icon: z.enum(StorefrontWidgetCartCardIcon).nullable(),
  iconColor: z.string(),
  iconBackgroundColor: z.string(),
  showIcons: z.boolean(),
  autoCheck: z.boolean(),
  clickListenerSelector: z.string().nullable(),
});

export const StorefrontWidgetCheckoutCheckboxSchema = ToggleTextSchema.extend({
  type: z.literal(StorefrontWidgetType.CheckoutCheckbox),
  titleTextSize: z.enum(StorefrontWidgetCheckoutTextSize).nullable(),
  subtextSize: z.enum(StorefrontWidgetCheckoutTextSize).nullable(),
  spacing: z.enum(StorefrontWidgetCheckoutSpacing),
  titleOnly: z.boolean(),
  returnsAutoCheck: z.boolean(),
  packageProtectionAutoCheck: z.boolean(),
});

export const StorefrontWidgetPdpInfoSchema = z.object({
  type: z.literal(StorefrontWidgetType.PdpInfo),
  html: z.string(),
  textOverride: z.string(),
  textAfterRedo: z.string(),
  showPrice: z.boolean(),
  fontSize: z.number(),
  textColor: z.string(),
  customCss: z.string(),
});

export const StorefrontWidgetDetailSchema = z.discriminatedUnion("type", [
  StorefrontWidgetInfoCardSchema,
  StorefrontWidgetBothCheckoutButtonsSchema,
  StorefrontWidgetCheckoutButtonSchema,
  StorefrontWidgetCartCheckboxSchema,
  StorefrontWidgetSingleToggleSchema,
  StorefrontWidgetDoubleToggleSchema,
  StorefrontWidgetCartCardSchema,
  StorefrontWidgetCheckoutCheckboxSchema,
  StorefrontWidgetPdpInfoSchema,
]);
export type StorefrontWidgetDetail = z.infer<
  typeof StorefrontWidgetDetailSchema
>;

export const StorefrontWidgetSchema = z.object({
  name: z.string().nullable(),
  enabled: z.boolean(),
  localizationId: z.string().nullable(),
  placements: z.array(StorefrontWidgetPlacementSchema),
  detail: StorefrontWidgetDetailSchema,
});
export type StorefrontWidget = z.infer<typeof StorefrontWidgetSchema>;

/** One arm's storefront coverage settings: the coverage_* and storefront_widget* rows without ids. */
export const StorefrontTreatmentSettingsSchema = z.object({
  infoModal: CoverageInfoModalSchema,
  settings: CoverageSettingsSchema,
  cartRefreshActions: z.array(CoverageCartRefreshActionSchema),
  cartModifications: z.array(CoverageCartModificationSchema),
  widgets: z.array(StorefrontWidgetSchema),
});
export type StorefrontTreatmentSettings = z.infer<
  typeof StorefrontTreatmentSettingsSchema
>;
